// Goal periods and report ranges in the browser's local calendar (ports of `GoalPeriod.start(from:)`,
// GroundTeam/Models/Engagement.swift:91, and `ReportEngine.DateRange`, GroundTeam/Services/ReportEngine.swift:5).
// Weeks start on the locale's first weekday (Sunday for en-US), like iOS `Calendar.current` (WEB_PLAN.md §5.10).

import type { GoalPeriod } from '@/contracts/enums';
import {
  DISTANT_PAST,
  addDays,
  endOfDay,
  resolveWeekStart,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  type WeekOptions,
} from './calendar';

export type { WeekOptions } from './calendar';

/** An inclusive instant range (`from <= t <= to`), the bounds `metricValue` and `team_metrics` take. */
export interface DateRange {
  from: Date;
  to: Date;
}

/** `GoalPeriod.start(from:)`: local midnight today, the week's first day, the 1st of the month or of the quarter. */
export function goalPeriodStart(period: GoalPeriod, now: Date = new Date(), opts: WeekOptions = {}): Date {
  switch (period) {
    case 'daily':
      return startOfDay(now);
    case 'weekly':
      return startOfWeek(now, resolveWeekStart(opts));
    case 'monthly':
      return startOfMonth(now);
    case 'quarterly':
      return startOfQuarter(now);
  }
}

/** The current period so far: `goalPeriodStart` → now (how goals, badges and leaderboards measure). */
export function goalPeriodRange(period: GoalPeriod, now: Date = new Date(), opts: WeekOptions = {}): DateRange {
  return { from: goalPeriodStart(period, now, opts), to: now };
}

/** `ReportEngine.DateRange` cases, in the iOS order. */
export const REPORT_RANGES = ['today', 'week', 'month', 'quarter', 'last30', 'last90', 'all'] as const;
export type ReportRangeKey = (typeof REPORT_RANGES)[number];

export const REPORT_RANGE_TITLES: Readonly<Record<ReportRangeKey, string>> = {
  today: 'Today',
  week: 'This Week',
  month: 'This Month',
  quarter: 'This Quarter',
  last30: 'Last 30 Days',
  last90: 'Last 90 Days',
  all: 'All Time',
};

/** `DateRange.start`: "last 30 days" keeps the time of day (`date(byAdding: .day, value: -30, to: now)`). */
export function reportRangeStart(range: ReportRangeKey, now: Date = new Date(), opts: WeekOptions = {}): Date {
  switch (range) {
    case 'today':
      return startOfDay(now);
    case 'week':
      return startOfWeek(now, resolveWeekStart(opts));
    case 'month':
      return startOfMonth(now);
    case 'quarter':
      return startOfQuarter(now);
    case 'last30':
      return addDays(now, -30);
    case 'last90':
      return addDays(now, -90);
    case 'all':
      return DISTANT_PAST;
  }
}

/** A report range ending now (iOS ranges have no upper bound; "now" is the same thing for past data). */
export function reportRange(range: ReportRangeKey, now: Date = new Date(), opts: WeekOptions = {}): DateRange {
  return { from: reportRangeStart(range, now, opts), to: now };
}

/** The web's custom range: from the first day's local midnight to the last second of the last day. */
export function customRange(firstDay: Date, lastDay: Date): DateRange {
  const a = startOfDay(firstDay);
  const b = endOfDay(lastDay);
  return a.getTime() <= b.getTime() ? { from: a, to: b } : { from: startOfDay(lastDay), to: endOfDay(firstDay) };
}

/** One calendar day, `repSummary` style: local midnight to `midnight + 1 day - 1 s`. */
export function dayRange(day: Date): DateRange {
  return { from: startOfDay(day), to: endOfDay(day) };
}
